import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Bell } from "@fleet/icons";
import { Card, CardContent, CardHeader, CardTitle, Skeleton, Switch } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@journal/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";

type Resp = components["schemas"]["NotificationPreferencesResponse"];

const CHANNELS = ["email", "push", "in_app"] as const;
const CATEGORIES = ["marketing", "product"] as const; // transactional no es opt-out-able

/**
 * User notification preferences panel (campaign #11). Allows
 * enabling/disabling each channel × category. The security transactional
 * one is not shown because it is always sent (verification/reset).
 */
export function NotificationPreferencesSection() {
  const { t } = useI18n();

  const prefsQuery = createApiQuery<Resp, components["schemas"]["ErrorModel"]>(
    () => ["notifications", "preferences"],
    async () => api.GET("/api/notifications/preferences"),
    () => ({ staleTime: 30_000 }),
  );

  const updateMutation = createApiMutation(
    async (vars: components["schemas"]["UpdatePreferenceDto"]) =>
      api.POST("/api/notifications/preferences", { body: vars }),
  );

  const prefMap = createMemo(() => {
    const map = new Map<string, boolean>();
    for (const p of prefsQuery.data?.preferences ?? []) {
      map.set(`${p.channel}:${p.category}`, p.enabled);
    }
    return map;
  });

  const isEnabled = (channel: string, category: string) =>
    prefMap().get(`${channel}:${category}`) ?? true;

  const toggle = async (channel: string, category: string, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ channel, category, enabled });
      await prefsQuery.refetch();
    } catch (err) {
      console.error("update preference error", err);
    }
  };

  const channelLabel = (c: string) =>
    c === "email" ? t(msg`Email`) : c === "push" ? t(msg`Push`) : t(msg`In-app`);
  const categoryLabel = (c: string) =>
    c === "marketing" ? t(msg`Marketing & newsletters`) : t(msg`Product updates`);

  return (
    <Card>
      <CardHeader>
        <CardTitle class="flex items-center gap-2">
          <Bell class="h-5 w-5 text-primary" />
          {t(msg`Notification preferences`)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* P23/CC-60 · I3: «no hay respuesta» ≠ «no hay nada». Sin este ramal
            un fallo de red se pintaba como una lista vacía. */}
        <Show when={prefsQuery.isError}>
          <QueryErrorState
            testId="notification-prefs-error"
            compact
            onRetry={() => void prefsQuery.refetch()}
          />
        </Show>
        <Show when={!prefsQuery.isError}>
          <Show when={!prefsQuery.isLoading} fallback={<Skeleton class="h-32 w-full rounded-xl" />}>
            <div class="space-y-6">
              <For each={CATEGORIES}>
                {(category) => (
                  <div>
                    <p class="mb-2 text-sm font-medium">{categoryLabel(category)}</p>
                    <div class="space-y-3">
                      <For each={CHANNELS}>
                        {(channel) => (
                          <div class="flex items-center justify-between">
                            <span class="text-sm text-muted-foreground">
                              {channelLabel(channel)}
                            </span>
                            <Switch
                              checked={isEnabled(channel, category)}
                              onCheckedChange={(v) => toggle(channel, category, v)}
                            />
                          </div>
                        )}
                      </For>
                    </div>
                  </div>
                )}
              </For>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Security emails (verification, password reset) are always sent and can't be disabled.`,
                )}
              </p>
            </div>
          </Show>
        </Show>
      </CardContent>
    </Card>
  );
}
